import React, { useEffect, useState } from 'react';
import { get, patch } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import SendRewardButton from '../../components/rewards/SendRewardButton';
import { FaBirthdayCake, FaCalendarAlt, FaBullseye, FaEye, FaEyeSlash } from 'react-icons/fa';

const AlertPage = () => {
    const [alerts, setAlerts] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [showReadAlerts, setShowReadAlerts] = useState(false);
    const { user } = useAuthStore();

    useEffect(() => {
        const fetchMarcasAndAlerts = async () => {
            if (!user?.userId) return;

            try {
                // Obtener las marcas asociadas al usuario
                const responseMarcas = await get(`marcas/user/${user.userId}`);
                setMarcas(responseMarcas);

                // Si hay marcas, obtener alertas para cada una
                if (responseMarcas.length > 0) {
                    const alertPromises = responseMarcas.map(async (marca) => {
                        try {
                            // Usar el parámetro estado=all según la nueva documentación
                            const result = await get(`alertas/marca/${marca._id}?estado=all`);
                            // El backend ahora devuelve { alertas: [...], pagination: {...} }
                            return result.alertas || result;
                        } catch (error) {
                            console.error('Error obteniendo alertas para marca:', error);
                            return [];
                        }
                    });
                    const allAlerts = (await Promise.all(alertPromises)).flat();
                    
                    // Ordenar alertas por fecha (más recientes primero)
                    const sortedAlerts = allAlerts.sort((a, b) => {
                        const dateA = new Date(a.createdAt || a.fecha || 0);
                        const dateB = new Date(b.createdAt || b.fecha || 0);
                        return dateB - dateA;
                    });
                    
                    setAlerts(sortedAlerts);
                }
            } catch (error) {
                console.error('Error al obtener marcas o alertas:', error);
            }
        };

        fetchMarcasAndAlerts();
    }, [user]);

    const markAsRead = async (alertId) => {
        try {
            await patch(`alertas/${alertId}`);
            setAlerts((prevAlerts) =>
                prevAlerts.map(alert =>
                    alert._id === alertId
                        ? { ...alert, leida: true }
                        : alert
                )
            );
        } catch (error) {
            console.error('Error al marcar la alerta como leída:', error);
        }
    };

    // Función para obtener el icono según el tipo de alerta
    const getAlertIcon = (tipo) => {
        switch (tipo) {
            case 'Cumpleaños':
                return <FaBirthdayCake className="text-pink-500" />;
            case 'Fecha Clave':
                return <FaCalendarAlt className="text-blue-500" />;
            case 'Objetivo':
                return <FaBullseye className="text-green-500" />;
            default:
                return <FaCalendarAlt className="text-gray-500" />;
        }
    };

    // Función para obtener el color de fondo según el tipo
    const getAlertBgColor = (tipo) => {
        switch (tipo) {
            case 'Cumpleaños':
                return 'bg-pink-50 border-pink-200';
            case 'Fecha Clave':
                return 'bg-blue-50 border-blue-200';
            case 'Objetivo':
                return 'bg-green-50 border-green-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    // Agrupar alertas por tipo
    const groupedAlerts = alerts.reduce((acc, alert) => {
        const tipo = alert.tipo || 'Otros';
        if (!acc[tipo]) {
            acc[tipo] = [];
        }
        acc[tipo].push(alert);
        return acc;
    }, {});

    // Separar alertas leídas y no leídas
    const unreadAlerts = alerts.filter(alert => !alert.leida);
    const readAlerts = alerts.filter(alert => alert.leida);

    // Formatear fecha
    const formatDate = (dateString) => {
        if (!dateString) return 'Fecha no disponible';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    return (
        <div className="container mx-auto p-4 max-w-6xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Centro de Alertas</h1>
                <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Mostrar leídas:</span>
                        <button
                            onClick={() => setShowReadAlerts(!showReadAlerts)}
                            className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm transition-colors ${showReadAlerts
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                                }`}
                        >
                            {showReadAlerts ? <FaEye /> : <FaEyeSlash />}
                            <span>{showReadAlerts ? 'Ocultar' : 'Mostrar'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Resumen de alertas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-red-600">No Leídas</p>
                            <p className="text-2xl font-bold text-red-700">{unreadAlerts.length}</p>
                        </div>
                        <FaEye className="text-red-500 text-xl" />
                    </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-green-600">Leídas</p>
                            <p className="text-2xl font-bold text-green-700">{readAlerts.length}</p>
                        </div>
                        <FaEyeSlash className="text-green-500 text-xl" />
                    </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-blue-600">Total</p>
                            <p className="text-2xl font-bold text-blue-700">{alerts.length}</p>
                        </div>
                        <FaCalendarAlt className="text-blue-500 text-xl" />
                    </div>
                </div>
            </div>

            {alerts.length === 0 ? (
                <div className="text-center py-12">
                    <FaCalendarAlt className="mx-auto text-6xl text-gray-300 mb-4" />
                    <h3 className="text-xl font-medium text-gray-600 mb-2">No hay alertas</h3>
                    <p className="text-gray-500">No hay alertas asociadas a tus marcas en este momento.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Alertas No Leídas */}
                    {unreadAlerts.length > 0 && (
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <FaEye className="mr-2 text-red-500" />
                                Alertas No Leídas ({unreadAlerts.length})
                            </h2>
                            <div className="space-y-4">
                                {Object.entries(groupedAlerts).map(([tipo, alertasTipo]) => {
                                    const alertasNoLeidas = alertasTipo.filter(alert => !alert.leida);
                                    if (alertasNoLeidas.length === 0) return null;

                                    return (
                                        <div key={tipo} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                            <div className={`px-4 py-3 border-b ${getAlertBgColor(tipo)}`}>
                                                <h3 className="font-semibold text-gray-800 flex items-center">
                                                    {getAlertIcon(tipo)}
                                                    <span className="ml-2">{tipo} ({alertasNoLeidas.length})</span>
                                                </h3>
                                            </div>
                                            <div className="divide-y divide-gray-100">
                                                {alertasNoLeidas.map((alert) => (
                                                    <div key={alert._id} className="p-4 hover:bg-gray-50 transition-colors">
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <p className="text-gray-800 font-medium mb-2">{alert.mensaje}</p>
                                                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                                    <span>Marca: {marcas.find(m => m._id === alert.marca)?.nombre || 'Desconocida'}</span>
                                                                    <span>•</span>
                                                                    <span>{formatDate(alert.createdAt || alert.fecha)}</span>
                                                                </div>
                                                                {alert.cliente && (
                                                                    <div className="mt-3">
                                                                        <SendRewardButton clienteId={alert.cliente} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => markAsRead(alert._id)}
                                                                className="ml-4 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
                                                            >
                                                                <FaEyeSlash />
                                                                <span>Marcar como leída</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Alertas Leídas */}
                    {showReadAlerts && readAlerts.length > 0 && (
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                                <FaEyeSlash className="mr-2 text-green-500" />
                                Alertas Leídas ({readAlerts.length})
                            </h2>
                            <div className="space-y-4">
                                {Object.entries(groupedAlerts).map(([tipo, alertasTipo]) => {
                                    const alertasLeidas = alertasTipo.filter(alert => alert.leida);
                                    if (alertasLeidas.length === 0) return null;

                                    return (
                                        <div key={`${tipo}-read`} className="bg-white rounded-lg border border-gray-200 overflow-hidden opacity-75">
                                            <div className={`px-4 py-3 border-b ${getAlertBgColor(tipo)}`}>
                                                <h3 className="font-semibold text-gray-600 flex items-center">
                                                    {getAlertIcon(tipo)}
                                                    <span className="ml-2">{tipo} ({alertasLeidas.length})</span>
                                                </h3>
                                            </div>
                                            <div className="divide-y divide-gray-100">
                                                {alertasLeidas.map((alert) => (
                                                    <div key={alert._id} className="p-4">
                                                        <div className="flex justify-between items-start">
                                                            <div className="flex-1">
                                                                <p className="text-gray-600 font-medium mb-2 line-through">{alert.mensaje}</p>
                                                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                                    <span>Marca: {marcas.find(m => m._id === alert.marca)?.nombre || 'Desconocida'}</span>
                                                                    <span>•</span>
                                                                    <span>{formatDate(alert.createdAt || alert.fecha)}</span>
                                                                    <span>•</span>
                                                                    <span className="text-green-600 font-medium">Leída</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AlertPage;
